import { ArrowDropDown, Clear } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React from 'react';
import AppSelect from '../../../form/AppSelect';
import { DateTimePicker } from '@mui/x-date-pickers';

import { Field, Form, Formik } from 'formik';
import { ACTIONS, initialValues, productsValidationSchema } from '../../Constants';
import { renderField } from '../../../../helpers/formik';

import useScheduleInboundController from '../../Hooks/useScheduleInboundController';
import { useAxios } from '../../../../hooks/useAxios';
import { NOTIFICATION_SUBMIT_ACTION } from '../../../../api/api';

const LabelWithChip = ({ label, chipLabel = 'SUGGESTED' }) => {
  return (
    <Box display="flex" alignItems="center" flexWrap="nowrap" gap={1}>
      <Typography>{label}</Typography>
      <Chip
        size="small"
        label={
          <Typography sx={{ color: 'white' }} fontWeight="bold">
            {chipLabel}
          </Typography>
        }
        color="secondary"
      />
    </Box>
  );
};

const ScheduleInbound = ({ data, open, closeActiveNotificationAction: onClose, onCloseNotification }) => {
  const [state, handlers, { formikRef, datePickerRef }] = useScheduleInboundController({
    onCloseNotification,
    onClose,
    data,
  });
  const { callAxios: submitDraftCreationAction, isLoading: isSubmittingDraftCreationAction } = useAxios({
    url: NOTIFICATION_SUBMIT_ACTION,
    method: 'post',
    callOnMount: false,
  });
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues({
        planned_arrival_date: data?.planned_arrival_date || '',
        consignment_number: data?.suggested_consignment_number || '',
        products: data?.products?.map((product) => ({
          consignment_quantity: product.suggested_quantity,
          sku: product.sku,
        })),
      })}
      validationSchema={productsValidationSchema}
    >
      {(formik) => {
        return (
          <Form>
            <Dialog onClose={onClose} maxWidth="sm" fullWidth open={open}>
              <DialogContent
                sx={{
                  minHeight: '400px',
                  maxHeight: '60vh',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
                  <Typography fontSize="large" fontWeight="bold">
                    Inbound ASN Request
                  </Typography>
                  <IconButton onClick={onClose}>
                    <Clear />
                  </IconButton>
                </Box>
                <Box
                  sx={{ overflowY: 'auto', overflowX: 'hidden' }}
                  display="grid"
                  flexDirection="column"
                  gap={6}
                  flexWrap="nowrap"
                >
                  <Box
                    sx={(theme) => ({
                      width: '50%',
                      [theme.breakpoints.down('sm')]: {
                        width: '100%',
                      },
                    })}
                  >
                    <Typography fontWeight="bold">Arrival Details</Typography>
                    <Field name="warehouse_id">
                      {renderField(AppSelect, {
                        error: formik?.errors?.warehouse_id,
                        IconComponent: (props) =>
                          state.isFetchingWarehouses ? (
                            <Box display="flex" alignItems="center" justifyContent="center">
                              <CircularProgress size={20} color="secondary" />
                            </Box>
                          ) : (
                            <ArrowDropDown {...props} style={{ color: 'rgb(0,0,0,0.54)' }} />
                          ),
                        disabled: state.isFetchingWarehouses || state.isSubmittingInbound,
                        fullWidth: true,
                        label: 'Warehouse',
                        options: state.warehousesOptions?.map((warehouse) => ({
                          label: warehouse?.name,
                          value: warehouse?.warehouse_id,
                        })),
                      })}
                    </Field>
                  </Box>
                  <Box>
                    <Field name="consignment_number">
                      {renderField(TextField, {
                        error: formik?.errors?.consignment_number,
                        disabled: state.isSubmittingInbound,
                        fullWidth: true,
                        label: <LabelWithChip label="Consignment Number" />,
                        sx: (theme) => ({
                          width: '50%',
                          [theme.breakpoints.down('sm')]: {
                            width: '100%',
                          },
                        }),
                        variant: 'standard',
                        autoComplete: 'off',
                      })}
                    </Field>
                  </Box>
                  <Box
                    onClick={() => {
                      if (!state.plannedArrivalDateOpen) handlers.setPlannedArrivalDateOpen(true);
                    }}
                  >
                    <Field name="planned_arrival_date">
                      {renderField(DateTimePicker, {
                        onChange: (date) => {
                          formik?.setFieldValue('planned_arrival_date', date);
                        },
                        defaultValue: new Date(data?.planned_arrival_date),
                        ref: datePickerRef,
                        onAccept: () => handlers.setPlannedArrivalDateOpen(false),
                        slotProps: {
                          popper: {
                            ref: datePickerRef,
                          },
                          textField: {
                            variant: 'standard',
                            error: formik?.errors?.planned_arrival_date,
                          },
                        },
                        sx: (theme) => ({
                          width: '50%',
                          button: { display: 'none' },
                          [theme.breakpoints.down('sm')]: {
                            width: '100%',
                          },
                        }),
                        label: <LabelWithChip label="Planned Arrival Date" />,
                        open: state.plannedArrivalDateOpen,
                        disabled: state.isSubmittingInbound,
                      })}
                    </Field>
                  </Box>
                  <Box>
                    <Typography mb={4} fontWeight="bold">
                      SKU Details
                    </Typography>

                    <Box display="flex" flexDirection="column" gap={3}>
                      {formik?.values?.products?.map((product, index) => (
                        <Box key={index} display="flex" justifyContent="space-between" alignItems="center">
                          <TextField
                            sx={{
                              input: {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                            variant="standard"
                            label="SKU Number"
                            value={product?.sku}
                            InputProps={{
                              readOnly: true,
                              disableUnderline: true,
                            }}
                          />
                          <Field name={`products[${index}].consignment_quantity`}>
                            {renderField(TextField, {
                              disabled: state.isSubmittingInbound,
                              sx: (theme) => ({
                                width: '40%',
                                [theme.breakpoints.down('sm')]: {
                                  width: '50%',
                                },
                              }),
                              label: <LabelWithChip label={isMobile ? 'Qty' : 'Quantity'} />,
                              variant: 'standard',
                              autoComplete: 'off',
                            })}
                          </Field>
                          {formik?.values?.products?.length > 1 ? (
                            <IconButton onClick={() => handlers.handleSkuDelete(product.sku)}>
                              <Clear sx={{ color: 'red' }} />
                            </IconButton>
                          ) : null}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
                <Box pt="48px" justifyContent="flex-end" mt="auto" display="flex" gap={3}>
                  <Button onClick={onClose} variant="contained" color="secondary" size="small">
                    Cancel
                  </Button>
                  <Button
                    disabled={
                      !(formik.isValid && formik.dirty) || state.isSubmittingInbound || isSubmittingDraftCreationAction
                    }
                    onClick={() => {
                      submitDraftCreationAction({
                        data: {
                          brand_id: data?.brand_id,
                          notification_message: data?.notificationMessageId,
                          consignment_id: formik?.values?.consignment_number,
                          action: ACTIONS.INBOUND_DRAFT_CREATION,
                        },
                      });

                      handlers.handleSubmit(true);
                    }}
                    variant="contained"
                    color="primary"
                    size="small"
                  >
                    Create Draft
                  </Button>
                </Box>
              </DialogContent>
            </Dialog>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ScheduleInbound;
